import { useCallback, useEffect, useState } from 'react'
import {
  adjustCreditBank,
  getCreditBank,
  revokeCreditBankEntry,
  type RemoteCreditBank,
  type RemoteCreditBankEntry
} from '../lib/adminClient'
import { REFERRAL_BANK_CAP_USD, REFERRAL_BANK_USD, tierName } from '../lib/tiers'
import { dateTime, day, usd } from '../lib/format'
import { isMissingEndpoint } from './LoadError'
import { DetailGrid, ExpandableRow, useOpenRow } from './RowDetail'

// The referral credit bank for one account (growth spec section 4b): US$12 per
// qualifying referral, capped at US$144 earned per rolling 12 months, spendable
// only on subscriptions. Separate from AI credits. An admin can adjust it (a
// reason is required and audit-logged by the server) and revoke an earned entry.

type Load = { s: 'loading' } | { s: 'ready'; bank: RemoteCreditBank } | { s: 'missing' } | { s: 'error'; error: string }

// What each kind does to the bank, for an entry's detail.
const KIND_MEANING: Record<string, string> = {
  earned: 'A referred account qualified, which added to the bank.',
  redeemed: 'A month of a plan was paid from the bank.',
  adjusted: 'An admin added to or took from the bank by hand.',
  pushed: 'Moved to their Stripe balance, where it comes off their next invoice.'
}

const KIND_LABEL: Record<string, string> = {
  earned: 'Referral earned',
  redeemed: 'Spent on plan',
  adjusted: 'Admin adjustment',
  pushed: 'Moved to Stripe balance'
}

export function CreditBankPanel({ accountId, canWrite }: { accountId: string; canWrite: boolean }): JSX.Element {
  const [load, setLoad] = useState<Load>({ s: 'loading' })
  const rowOpen = useOpenRow()
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const [revoking, setRevoking] = useState<RemoteCreditBankEntry | null>(null)
  const [revokeReason, setRevokeReason] = useState('')

  const fetchBank = useCallback(async () => {
    const res = await getCreditBank(accountId)
    if (res.ok) setLoad({ s: 'ready', bank: res.data })
    else if (isMissingEndpoint(res.status, res.error)) setLoad({ s: 'missing' })
    else setLoad({ s: 'error', error: res.error })
  }, [accountId])

  useEffect(() => {
    setLoad({ s: 'loading' })
    void fetchBank()
  }, [fetchBank])

  const parsed = Number(amount)
  const amountValid = /^-?\d+(\.\d{1,2})?$/.test(amount.trim()) && parsed !== 0
  const reasonValid = reason.trim().length >= 3

  async function adjust(): Promise<void> {
    if (!amountValid || !reasonValid || busy) return
    setBusy(true)
    setNotice(null)
    const res = await adjustCreditBank(accountId, parsed, reason.trim())
    setBusy(false)
    if (!res.ok) {
      setNotice({ tone: 'error', text: `Adjustment failed: ${res.error}` })
      return
    }
    setLoad({ s: 'ready', bank: res.data })
    setNotice({ tone: 'ok', text: `Adjusted by ${usd(parsed)}. Bank now ${usd(res.data.balanceUsd)}.` })
    setAmount('')
    setReason('')
  }

  async function revoke(): Promise<void> {
    if (!revoking || revokeReason.trim().length < 3 || busy) return
    setBusy(true)
    setNotice(null)
    const res = await revokeCreditBankEntry(accountId, revoking.id, revokeReason.trim())
    setBusy(false)
    if (!res.ok) {
      setNotice({ tone: 'error', text: `Revoke failed: ${res.error}` })
      return
    }
    setLoad({ s: 'ready', bank: res.data })
    setNotice({ tone: 'ok', text: `Revoked ${usd(revoking.usd)}. Bank now ${usd(res.data.balanceUsd)}.` })
    setRevoking(null)
    setRevokeReason('')
  }

  if (load.s === 'loading') return <div className="text-xs text-slate-500">Loading credit bank…</div>
  if (load.s === 'missing') {
    return (
      <div className="text-xs text-amber-300" data-testid="credit-bank-missing">
        This server does not provide <code>GET /admin/users/:id/credit-bank</code> yet, so the referral credit bank cannot be shown or changed.
      </div>
    )
  }
  if (load.s === 'error') return <div className="text-xs text-red-300" role="alert">Could not load the credit bank: {load.error}</div>

  const b = load.bank
  const capUsd = b.capUsd || REFERRAL_BANK_CAP_USD
  return (
    <div className="space-y-4" data-testid="credit-bank">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-slate-400">
        <div>
          <div className="uppercase tracking-wider mb-0.5">Bank balance</div>
          <div className="text-lg font-semibold text-slate-100 tabular-nums" data-testid="credit-bank-balance">{usd(b.balanceUsd)}</div>
        </div>
        <div>
          <div className="uppercase tracking-wider mb-0.5">Earned, last 12 months</div>
          <div className={`text-lg font-semibold tabular-nums ${b.earnedUsd12m >= capUsd ? 'text-amber-300' : 'text-slate-100'}`} data-testid="credit-bank-earned">
            {usd(b.earnedUsd12m)} <span className="text-xs text-slate-500 font-normal">of {usd(capUsd)}</span>
          </div>
        </div>
        <div>
          <div className="uppercase tracking-wider mb-0.5">Qualified referrals</div>
          <div className="text-lg font-semibold text-slate-100 tabular-nums">{b.qualified}</div>
        </div>
        <div>
          <div className="uppercase tracking-wider mb-0.5">Plan paid by credit</div>
          <div className="text-slate-100" data-testid="credit-bank-funded">
            {b.planFundedByCredit ? `${tierName(b.planFundedByCredit)} until ${day(b.creditPaidUntil)}` : 'none'}
          </div>
        </div>
      </div>
      {typeof b.pendingCreditUsd === 'number' && b.pendingCreditUsd > 0 && (
        <div className="text-xs text-amber-300">{usd(b.pendingCreditUsd)} recorded as pending credit (Stripe not configured on this server).</div>
      )}
      <div className="text-[11px] text-slate-500">
        {usd(REFERRAL_BANK_USD)} per referral who joins free and verifies; at most {usd(capUsd)} per rolling 12 months. Spendable on subscriptions only; not AI credits, not cash.
      </div>

      {b.entries.length === 0 ? (
        <div className="text-xs text-slate-500">No bank activity.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="admin-table" data-testid="credit-bank-entries">
            <thead>
              <tr>
                <th>When</th>
                <th>Entry</th>
                <th className="text-right">Amount</th>
                <th>Detail</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {b.entries.map((e) => (
                <ExpandableRow
                  key={e.id}
                  open={rowOpen.isOpen(e.id)}
                  onToggle={() => rowOpen.toggle(e.id)}
                  colSpan={5}
                  className={e.revokedAt ? 'opacity-50' : ''}
                  testId={`credit-bank-row-${e.id}`}
                  detail={() => (
                    <DetailGrid
                      facts={[
                        ['When', dateTime(e.at)],
                        ['Entry', KIND_LABEL[e.kind] ?? e.kind],
                        ['Amount', usd(e.usd)],
                        ['Plan', e.plan ? tierName(e.plan) : null],
                        ['Referred account', e.referredEmail ?? null],
                        ['Referred account id', e.referredAccountId ? <span className="font-mono">{e.referredAccountId}</span> : null],
                        ['By admin', e.adminEmail ?? null],
                        ['Revoked', e.revokedAt ? dateTime(e.revokedAt) : 'No'],
                        !!e.revokedAt && ['Why revoked', e.revokeReason ?? null],
                        ['Entry id', <span className="font-mono">{e.id}</span>]
                      ]}
                      wide={[['What it means', KIND_MEANING[e.kind] ?? e.kind], ...(e.reason ? [['Reason given', e.reason] as [string, string]] : [])]}
                    />
                  )}
                >
                  <td className="whitespace-nowrap text-slate-400">{dateTime(e.at)}</td>
                  <td>{KIND_LABEL[e.kind] ?? e.kind}</td>
                  <td className={`text-right tabular-nums ${e.usd < 0 ? 'text-red-300' : 'text-emerald-300'}`}>{usd(e.usd)}</td>
                  <td className="text-slate-400 text-[11px]">
                    {e.referredEmail ?? e.referredAccountId ?? (e.plan ? tierName(e.plan) : '')}
                    {e.reason && <div>“{e.reason}”{e.adminEmail ? ` by ${e.adminEmail}` : ''}</div>}
                    {e.revokedAt && <div className="text-red-300">revoked {day(e.revokedAt)}{e.revokeReason ? `: ${e.revokeReason}` : ''}</div>}
                  </td>
                  <td className="text-right">
                    {canWrite && e.kind === 'earned' && !e.revokedAt && (
                      <button
                        className="px-2 py-1 rounded-md text-[11px] border border-red-500/30 text-red-300 hover:bg-red-500/10"
                        onClick={() => { setRevoking(e); setRevokeReason('') }}
                        data-testid={`credit-bank-revoke-${e.id}`}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </ExpandableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {revoking && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs" data-testid="credit-bank-revoke-form">
          <div className="text-red-200 mb-2">
            Revoke {usd(revoking.usd)} earned {day(revoking.at)}{revoking.referredEmail ? ` for ${revoking.referredEmail}` : ''}? The amount comes back out of the bank.
          </div>
          <div className="flex gap-2">
            <input
              value={revokeReason}
              onChange={(ev) => setRevokeReason(ev.target.value)}
              placeholder="Reason (required, audit-logged)"
              aria-label="Revoke reason"
              className="flex-1 bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-accent"
            />
            <button className="btn-ghost" onClick={() => setRevoking(null)}>Cancel</button>
            <button className="btn-primary" disabled={revokeReason.trim().length < 3 || busy} onClick={() => void revoke()} data-testid="credit-bank-revoke-confirm">
              {busy ? 'Revoking…' : 'Revoke'}
            </button>
          </div>
        </div>
      )}

      {canWrite && (
        <div className="pt-3 border-t border-white/5">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Adjust bank</div>
          <div className="flex gap-2 items-start flex-wrap">
            <label className="block">
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.-]/g, ''))}
                placeholder="e.g. 12 or -12"
                aria-label="Adjustment in US dollars"
                data-testid="credit-bank-amount"
                className="w-32 bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:border-accent"
              />
              <div className="text-[11px] text-slate-500 mt-1">US$, signed, to the cent</div>
            </label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (required, audit-logged)"
              aria-label="Adjustment reason"
              data-testid="credit-bank-reason"
              className="flex-1 min-w-[180px] bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-accent"
            />
            <button className="btn-primary" disabled={!amountValid || !reasonValid || busy} onClick={() => void adjust()} data-testid="credit-bank-adjust">
              {busy ? 'Saving…' : 'Adjust'}
            </button>
          </div>
        </div>
      )}
      {notice && (
        <div role={notice.tone === 'error' ? 'alert' : 'status'} data-testid="credit-bank-notice" className={`text-xs ${notice.tone === 'error' ? 'text-red-300' : 'text-emerald-300'}`}>
          {notice.text}
        </div>
      )}
    </div>
  )
}

export function CreditBankModal({ accountId, title, canWrite, onClose }: { accountId: string; title: string; canWrite: boolean; onClose: () => void }): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={onClose}>
      <div className="glass-card rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`Referral credit bank for ${title}`}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Referral credit bank</div>
            <h2 className="text-lg font-semibold mt-1">{title}</h2>
          </div>
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
        <CreditBankPanel accountId={accountId} canWrite={canWrite} />
      </div>
    </div>
  )
}
